import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  TICKET_PRIORITIES,
  TICKET_PRIORITY_LABELS,
  TICKET_STATUSES,
  TICKET_STATUS_LABELS,
  type ConversationDetailDTO,
  type TicketPriority,
  type TicketStatus,
} from '@sac/contracts'
import { api } from '../../lib/api'
import { qk } from '../../lib/query'
import { useUi } from '../../state/UiContext'
import { Icon } from '../icons'
import { useToast } from '../ui/Toaster'

/**
 * Tickets for the customer in this conversation.
 *
 * Scoped to the customer, not the conversation: the whole point of a ticket is that it survives
 * the thread it started in. A ticket opened from a Shopee chat still has to be visible when the
 * same person writes on WhatsApp.
 */
export function TicketsTab({
  conv,
  agents,
  orders,
}: {
  conv: ConversationDetailDTO
  agents: { id: string; name: string }[]
  orders: { id: string; externalOrderId: string }[]
}) {
  const { state, dispatch } = useUi()
  const qc = useQueryClient()
  const toast = useToast()
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [owner, setOwner] = useState(agents[0]?.id ?? '')
  const [priority, setPriority] = useState<TicketPriority>('normal')
  const [orderId, setOrderId] = useState(conv.primaryOrderId ?? '')

  const { data: tickets } = useQuery({
    queryKey: qk.ticketsForCustomer(conv.customerId),
    queryFn: () => api.tickets({ customerId: conv.customerId }),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: qk.ticketsForCustomer(conv.customerId) })
    qc.invalidateQueries({ queryKey: ['tickets', 'all'] })
  }

  const createMutation = useMutation({
    mutationFn: () =>
      api.createTicket({
        customerId: conv.customerId,
        conversationId: conv.id,
        subject,
        description: description || null,
        assignedAgentId: owner || null,
        orderId: orderId || null,
        priority,
      }),
    onSuccess: (t) => {
      invalidate()
      qc.invalidateQueries({ queryKey: qk.conversation(conv.id) })
      dispatch({ type: 'setTicketForm', open: false })
      setSubject('')
      setDescription('')
      toast(`Ticket #${t.number} criado`)
    },
  })

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; status?: TicketStatus; priority?: TicketPriority }) =>
      api.updateTicket(input.id, { status: input.status, priority: input.priority }),
    onSuccess: () => invalidate(),
  })

  const list = tickets ?? []

  return (
    <>
      {state.ticketForm ? (
        <div className="ocard" style={{ padding: 12 }}>
          <div className="blk-h" style={{ marginBottom: 9 }}>
            Novo ticket
          </div>
          <div className="field">
            <label>Assunto</label>
            <input
              placeholder="Ex.: Cliente pede reimpressão por defeito"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              autoFocus
            />
          </div>
          <div className="field">
            <label>Descrição</label>
            <textarea
              rows={3}
              placeholder="Contexto que a próxima pessoa precisa para resolver sem reler a conversa"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="f2">
            <div className="field">
              <label>Responsável</label>
              <select value={owner} onChange={(e) => setOwner(e.target.value)}>
                <option value="">Ninguém</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Prioridade</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value as TicketPriority)}>
                {TICKET_PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {TICKET_PRIORITY_LABELS[p]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="field">
            <label>Pedido vinculado</label>
            <select value={orderId} onChange={(e) => setOrderId(e.target.value)}>
              <option value="">Nenhum</option>
              {orders.map((o) => (
                <option key={o.id} value={o.id}>
                  #{o.externalOrderId}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 7 }}>
            <button className="btn pri sm" disabled={!subject.trim() || createMutation.isPending} onClick={() => createMutation.mutate()}>
              Salvar
            </button>
            <button className="btn ghost sm" onClick={() => dispatch({ type: 'setTicketForm', open: false })}>
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button
          className="btn sec sm"
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          onClick={() => dispatch({ type: 'setTicketForm', open: true })}
        >
          <Icon name="plus" size={13} />
          Criar ticket
        </button>
      )}

      {list.length > 0 && (
        <>
          <div className="blk-h" style={{ margin: '16px 0 8px' }}>
            Tickets deste cliente<span className="cnt">{list.length}</span>
          </div>
          {list.map((t) => (
            <div className={`tkcard ${t.status === 'resolved' ? 'done' : ''}`} key={t.id}>
              <div className="tk-h">
                <span className="tk-num">#{t.number}</span>
                <span className={`pri ${t.priority}`}>{TICKET_PRIORITY_LABELS[t.priority]}</span>
              </div>
              <div className="tk-subj">{t.subject}</div>
              {t.description && <div className="tk-desc">{t.description}</div>}
              <div className="tm">
                {t.assignedAgentName && (
                  <span>
                    <span className="avatar" style={{ width: 16, height: 16, fontSize: 8, background: '#8b919d' }}>
                      {t.assignedAgentName.slice(0, 2).toUpperCase()}
                    </span>
                    {t.assignedAgentName}
                  </span>
                )}
                {t.orderNumber && (
                  <span>
                    <Icon name="box" size={11} />#{t.orderNumber}
                  </span>
                )}
                <span>
                  <Icon name="clock" size={11} />
                  {new Date(t.createdAt).toLocaleDateString('pt-BR')}
                </span>
              </div>
              <select
                className="tk-status"
                value={t.status}
                onChange={(e) => updateMutation.mutate({ id: t.id, status: e.target.value as TicketStatus })}
              >
                {TICKET_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {TICKET_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </>
      )}
      {!list.length && !state.ticketForm && (
        <div className="empty" style={{ padding: '26px 10px' }}>
          Nenhum ticket para este cliente.
          <br />
          Abra um para acompanhar algo que não se resolve nesta conversa.
        </div>
      )}
    </>
  )
}
